import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Mic, FileText, Camera } from "lucide-react";

const mediaOpportunities = [
  {
    icon: Mic,
    title: "Interviews",
    description:
      "Connect with 3E Entertainment leadership for interviews on Nepali cultural programming, diaspora identity, and community impact in America.",
  },
  {
    icon: Camera,
    title: "Event Coverage",
    description:
      "Cover flagship events like DTFEST, national artist tours, and community celebrations that draw thousands of attendees across the United States.",
  },
  {
    icon: FileText,
    title: "Press Features",
    description:
      "Tell compelling stories about heritage preservation, youth engagement, and the growing influence of Nepali culture in American society.",
  },
];

const Media = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="media-page"
    >
      {/* Hero */}
      <section className="media-hero">
        <div className="mh-overlay" />
        <motion.div
          className="mh-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Media Resources & Press Inquiries
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            3E Entertainment is available for press coverage, interviews, media
            features, and cultural commentary related to Nepali entertainment
            and diaspora identity in the United States.
          </motion.p>
        </motion.div>
      </section>

      {/* Media Intro */}
      <section className="media-intro-section">
        <div className="m-container">
          <div className="media-intro-layout">
            <motion.div
              className="media-intro-text"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>A Story Rooted in Culture, Community, and Purpose</h2>
              <p>
                Our story is rooted in culture, community, and purpose. From
                large-scale festivals to artist tours and charitable initiatives,
                3E Entertainment continues to shape the future of Nepali
                cultural experiences in America through platforms that preserve
                heritage while expanding its reach.
              </p>
              <p>
                We offer unique access to narratives about the Nepali diaspora,
                second-generation identity, the role of arts and entertainment
                in community building, and the intersection of cultural
                preservation and modern celebration.
              </p>
            </motion.div>

            <motion.div
              className="media-contact-card"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="mcc-accent" />
              <h3>Media Contact</h3>
              <p>
                For interviews, press requests, and media opportunities, please
                contact our team directly. We aim to respond to all media
                inquiries within 48 hours.
              </p>
              <div className="mcc-detail">
                <Mail size={16} />
                <a href="mailto:info@3etainment.com">info@3etainment.com</a>
              </div>
              <Link to="/contact" className="mcc-btn">
                Send Media Inquiry <ArrowRight size={15} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Media Opportunities */}
      <section className="media-opportunities-section">
        <div className="m-container">
          <div className="mo-header">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              How We Can Work Together
            </motion.h2>
          </div>

          <div className="mo-grid">
            {mediaOpportunities.map((opp, index) => {
              const Icon = opp.icon;
              return (
                <motion.div
                  key={opp.title}
                  className="mo-card"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.12 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6, transition: { duration: 0.3 } }}
                >
                  <div className="mo-icon">
                    <Icon size={22} />
                  </div>
                  <h4>{opp.title}</h4>
                  <p>{opp.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Coverage Topics */}
      <section className="topics-section">
        <div className="m-container">
          <motion.div
            className="topics-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h3>Key Topics We Cover</h3>
            <div className="topics-grid">
              {[
                "Nepali entertainment in America",
                "Diaspora identity and belonging",
                "Youth engagement and second-generation culture",
                "Cross-cultural programming",
                "Community giving and the 20% model",
                "DTFEST and flagship events",
                "Artist tours and cultural programming",
                "Heritage preservation through entertainment",
              ].map((topic) => (
                <span key={topic} className="topic-tag">{topic}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="media-cta-section">
        <div className="mcs-overlay" />
        <motion.div
          className="mcs-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>Ready to Tell the Story?</h2>
          <p>
            For interviews, press requests, event coverage credentials, and
            media opportunities, please reach out to our team. We welcome
            journalists, documentarians, bloggers, and content creators who
            want to amplify Nepali cultural stories.
          </p>
          <Link to="/contact" className="mcs-btn">
            Contact Our Media Team <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>

      <style>{`
        .media-page { overflow-x: hidden; }

        .media-hero {
          position: relative;
          min-height: 65vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/R5BI6877.webp") center/cover no-repeat;
        }

        .mh-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.82);
        }

        .mh-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .mh-content h1 {
          font-size: clamp(2.2rem, 5vw, 3.5rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
          line-height: 1.1;
        }

        .mh-content p {
          font-size: 1.15rem;
          color: rgba(255, 255, 255, 0.85);
          line-height: 1.7;
          max-width: 580px;
          margin: 0 auto;
        }

        .m-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }


        /* Media Intro */
        .media-intro-section {
          padding: 7rem 0;
          background: #fff;
        }

        .media-intro-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5rem;
          align-items: start;
        }

        .media-intro-text h2 {
          font-size: clamp(2rem, 3.5vw, 2.6rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1.25rem;
          line-height: 1.1;
        }

        .media-intro-text p {
          font-size: 1rem;
          color: #555;
          line-height: 1.8;
          margin: 0 0 1rem;
        }

        .media-contact-card {
          background: #fff;
          border: 1px solid #f0f0f0;
          border-radius: 20px;
          padding: 2.5rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
          position: relative;
          overflow: hidden;
        }

        .mcc-accent {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: #C72631;
        }

        .media-contact-card h3 {
          font-size: 1.3rem;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 1rem;
        }

        .media-contact-card p {
          font-size: 0.95rem;
          color: #666;
          line-height: 1.75;
          margin: 0 0 1.5rem;
        }

        .mcc-detail {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 2rem;
          color: #C72631;
        }

        .mcc-detail a {
          color: #0a0a0a;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          transition: color 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .mcc-detail a:hover {
          color: #C72631;
        }

        .mcc-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #C72631;
          color: #fff;
          padding: 0.85rem 1.75rem;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 700;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          transition: background 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s ease;
        }

        .mcc-btn:hover {
          background: #a01f28;
          transform: translateY(-2px);
        }

        /* Media Opportunities */
        .media-opportunities-section {
          padding: 6rem 0;
          background: #f7f7f7;
        }

        .mo-header {
          text-align: center;
          max-width: 600px;
          margin: 0 auto 4rem;
        }

        .mo-header h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0;
          line-height: 1.1;
        }

        .mo-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }

        .mo-card {
          background: #fff;
          border: 1px solid #f0f0f0;
          border-radius: 16px;
          padding: 2.5rem 2rem;
          box-shadow: 0 2px 15px rgba(0, 0, 0, 0.04);
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          cursor: default;
        }

        .mo-card:hover {
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
          border-color: #C72631;
        }

        .mo-icon {
          width: 52px;
          height: 52px;
          background: rgba(199, 38, 49, 0.08);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #C72631;
          margin-bottom: 1.5rem;
        }

        .mo-card h4 {
          font-size: 1.15rem;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 0.75rem;
        }

        .mo-card p {
          font-size: 0.93rem;
          color: #666;
          line-height: 1.75;
          margin: 0;
        }

        /* Topics */
        .topics-section {
          padding: 5rem 0;
          background: #fff;
        }

        .topics-box {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
        }

        .topics-box h3 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 2rem;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          font-size: 0.85rem;
        }

        .topics-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          justify-content: center;
        }

        .topic-tag {
          display: inline-block;
          background: #f7f7f7;
          color: #444;
          padding: 0.55rem 1.1rem;
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 500;
          border: 1px solid #eee;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .topic-tag:hover {
          background: rgba(199, 38, 49, 0.08);
          color: #C72631;
          border-color: rgba(199, 38, 49, 0.2);
        }

        /* CTA */
        .media-cta-section {
          position: relative;
          padding: 7rem 2rem;
          background: url("/images/R5BI6467.webp") center/cover no-repeat;
          text-align: center;
        }

        .mcs-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .mcs-content {
          position: relative;
          z-index: 2;
          max-width: 680px;
          margin: 0 auto;
        }

        .mcs-content h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #fff;
          margin: 0 0 1.25rem;
        }

        .mcs-content p {
          font-size: 1.05rem;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.75;
          margin: 0 0 2.5rem;
        }

        .mcs-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #C72631;
          color: #fff;
          padding: 1rem 2.25rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: background 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s ease;
        }

        .mcs-btn:hover {
          background: #a01f28;
          transform: translateY(-3px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .media-intro-layout {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .mo-grid {
            grid-template-columns: 1fr;
            max-width: 460px;
            margin: 0 auto;
          }
        }

        @media (max-width: 768px) {
          .media-intro-section,
          .media-opportunities-section,
          .topics-section {
            padding: 4.5rem 0;
          }

          .m-container {
            padding: 0 1.5rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Media;
